import React from "react";
import styled, { css, keyframes } from "styled-components";

export const C = {
  teal: "#00838c",
  tealDark: "#006670",
  tealLight: "#00a89b",
  tealAlpha: "rgba(0,131,140,0.10)",
  tealLine: "rgba(0,131,140,0.22)",
  navy: "#293461",
  navyDark: "#1b2244",
  navyDeep: "#0e1530",
  navyAlpha: "rgba(41,52,97,0.07)",
  navyLine: "rgba(41,52,97,0.14)",
  muted: "#949ab0",
  mutedMid: "#6b7290",
  mutedLight: "#b8bdd0",
  white: "#ffffff",
  surface: "#f5f6fa",
  line: "#e6e9f4",
  text: "#1a2040",
  textSub: "#4a5272",
  error: "#f44336",
  errorPale: "rgba(192,57,43,0.08)",
  amber: "#F6B436",
  amberAlpha: "rgba(246,180,54,0.12)",
};

export const SEMI = "openSans-SemiBold, sans-serif";
export const REG = "openSans-Regular, sans-serif";

/* ─── Keyframes ──────────────────────────────────────────────────────────── */

export const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

export const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.92); }
  to   { opacity: 1; transform: scale(1); }
`;

export const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const staggerIn = keyframes`
  from { opacity: 0; transform: translateX(-8px); }
  to   { opacity: 1; transform: translateX(0); }
`;

export const checkPop = keyframes`
  0%   { transform: scale(0) rotate(-12deg); }
  65%  { transform: scale(1.18) rotate(3deg); }
  100% { transform: scale(1) rotate(0deg); }
`;

export const pop = keyframes`
  0%   { transform: scale(0.6); }
  65%  { transform: scale(1.15); }
  100% { transform: scale(1); }
`;

export const fillBar = keyframes`
  from { transform: scaleX(0); }
  to   { transform: scaleX(1); }
`;

export const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const floatY = keyframes`
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-6px); }
`;

export const pulse = keyframes`
  0%   { box-shadow: 0 0 0 0    rgba(0,131,140,0.4); }
  70%  { box-shadow: 0 0 0 10px rgba(0,131,140,0); }
  100% { box-shadow: 0 0 0 0    rgba(0,131,140,0); }
`;

/* ─── Page Header (new) ──────────────────────────────────────────────────── */

export const PageHeader = styled.div`
  background: linear-gradient(
    135deg,
    ${C.navy} 0%,
    ${C.navyDark} 60%,
    ${C.navyDeep} 100%
  );
  border-radius: 20px 20px 0 0;
  padding: 28px 32px 24px;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;

  &::before {
    content: "";
    position: absolute;
    top: -60px;
    right: -60px;
    width: 220px;
    height: 220px;
    border-radius: 50%;
    background: rgba(0, 131, 140, 0.12);
    pointer-events: none;
  }
  &::after {
    content: "";
    position: absolute;
    bottom: -80px;
    left: -40px;
    width: 240px;
    height: 240px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.03);
    pointer-events: none;
  }

  @media (max-width: 640px) {
    padding: 22px 20px 20px;
    border-radius: 16px 16px 0 0;
  }
  @media (max-width: 400px) {
    padding: 18px 16px 16px;
  }
`;

export const PageHeaderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
  position: relative;
  z-index: 1;
`;

export const PageHeaderBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 99px;
  background: rgba(0, 131, 140, 0.2);
  border: 1px solid rgba(0, 131, 140, 0.35);
  font-family: ${SEMI};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.8);

  &::before {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${C.tealLight};
    box-shadow: 0 0 0 2px rgba(0, 168, 155, 0.3);
  }
`;

export const PageHeaderTitle = styled.h1`
  font-family: ${SEMI};
  font-size: clamp(18px, 3vw, 24px);
  font-weight: 800;
  color: ${C.white};
  letter-spacing: -0.03em;
  margin: 0 0 6px;
  line-height: 1.15;
  position: relative;
  z-index: 1;
`;

export const PageHeaderDesc = styled.p`
  font-family: ${REG};
  font-size: 13px;
  color: rgba(255, 255, 255, 0.55);
  line-height: 1.65;
  margin: 0;
  max-width: 480px;
  position: relative;
  z-index: 1;
`;

/* ─── Legacy SectionTop (kept for compat, now renders minimal) ─────────── */

export const SectionTop = styled.div`
  display: none; /* Header is now in PageHeader; hide old inline header */
`;

export const Eyebrow = styled.div`
  font-family: ${SEMI};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${C.muted};
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  &::before,
  &::after {
    content: "";
    display: block;
    width: 28px;
    height: 1px;
    background: ${C.line};
  }
`;

export const PageTitle = styled.h2`
  font-family: ${SEMI};
  font-size: clamp(20px, 3vw, 26px);
  font-weight: 700;
  color: ${C.navy};
  letter-spacing: -0.025em;
  margin-bottom: 8px;
  line-height: 1.2;
`;

export const PageDesc = styled.p`
  font-family: ${REG};
  font-size: 14px;
  color: ${C.textSub};
  line-height: 1.7;
  max-width: 500px;
  margin: 0 auto;
`;

export const Section = styled.div`
  margin-bottom: 32px;
  animation: ${fadeUp} 0.38s ease both;
  animation-delay: ${(p) => p.$delay || "0s"};
`;

export const HDivider = styled.div`
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    ${C.line} 20%,
    ${C.line} 80%,
    transparent
  );
  margin: 0 0 28px;
`;

export const SegLabel = styled.div`
  font-family: ${SEMI};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${C.mutedMid};
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 10px;

  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: ${C.line};
  }
`;

export const TabStrip = styled.div`
  display: flex;
  border: 1.5px solid ${C.line};
  border-radius: 14px;
  overflow: hidden;
  margin-bottom: 24px;
  background: ${C.surface};
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

export const TabButton = styled.button`
  flex: 1;
  padding: 18px 14px;
  border: none;
  background: ${(p) => (p.$active ? C.navy : "transparent")};
  cursor: pointer;
  text-align: center;
  position: relative;
  transition: all 0.24s ease;
  border-right: 1.5px solid ${C.line};
  &:last-child {
    border-right: none;
  }
  @media (max-width: 480px) {
    border-right: none;
    border-bottom: 1.5px solid ${C.line};
    &:last-child {
      border-bottom: none;
    }
  }
  &:hover:not(:disabled) {
    background: ${(p) => (p.$active ? C.navy : C.navyAlpha)};
  }
`;

export const TabLabel = styled.div`
  font-family: ${SEMI};
  font-weight: 700;
  font-size: 14px;
  color: ${(p) => (p.$active ? C.white : C.navy)};
  margin-bottom: 4px;
  transition: color 0.2s;
  letter-spacing: -0.01em;
`;

export const TabSub = styled.div`
  font-family: ${REG};
  font-size: 12px;
  color: ${(p) => (p.$active ? "rgba(255,255,255,0.58)" : C.muted)};
  transition: color 0.2s;
`;

export const TabActiveBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 2.5px;
  background: ${C.teal};
  transform-origin: left;
  animation: ${(p) =>
    p.$active
      ? css`
          ${fillBar} 0.28s ease both
        `
      : "none"};
  opacity: ${(p) => (p.$active ? 1 : 0)};
`;

const CounterWrap = styled.div`
  display: flex;
  align-items: center;
  border: 1.5px solid ${C.line};
  border-radius: 10px;
  overflow: hidden;
  background: ${C.white};
`;

const CounterBtn = styled.button`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${C.surface};
  border: none;
  font-size: 18px;
  color: ${C.mutedMid};
  cursor: pointer;
  font-family: ${SEMI};
  line-height: 1;
  flex-shrink: 0;
  transition: all 0.15s;
  &:hover:not(:disabled) {
    background: ${C.tealAlpha};
    color: ${C.teal};
  }
  &:disabled {
    color: ${C.line};
    cursor: not-allowed;
  }
`;

const CounterValue = styled.div`
  min-width: 44px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${SEMI};
  font-weight: 700;
  font-size: 15px;
  color: ${C.navy};
  background: ${C.white};
  border-left: 1px solid ${C.line};
  border-right: 1px solid ${C.line};
`;

export const Counter = ({ value, min = 0, max = 99, onChange }) => (
  <CounterWrap>
    <CounterBtn
      type="button"
      onClick={() => value > min && onChange(value - 1)}
      disabled={value <= min}
    >
      −
    </CounterBtn>
    <CounterValue>{value}</CounterValue>
    <CounterBtn
      type="button"
      onClick={() => value < max && onChange(value + 1)}
      disabled={value >= max}
    >
      +
    </CounterBtn>
  </CounterWrap>
);

export const FieldBlock = styled.div`
  display: flex;
  flex-direction: column;
`;

export const FloatLabel = styled.label`
  font-family: ${SEMI};
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${C.mutedMid};
  margin-bottom: 2px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const RequiredStar = styled.span`
  color: #ff3c3c;
  font-size: 12px;
  line-height: 1;
`;

export const StyledInput = styled.input`
  width: 100%;
  padding: 13px ${(p) => (p.$hasIcon ? "46px" : "16px")} 13px 16px;
  border-radius: 10px;
  border: 1.5px solid ${(p) => (p.$err ? C.error : C.line)};
  font-family: ${SEMI};
  font-size: 14px;
  color: ${C.navy};
  background: ${C.surface};
  outline: none;
  transition: all 0.22s;
  box-sizing: border-box;
  appearance: none;

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus {
    -webkit-box-shadow: 0 0 0 999px ${C.surface} inset !important;
    -webkit-text-fill-color: ${C.navy} !important;
    transition: background-color 9999s ease-in-out 0s;
  }

  &::placeholder {
    color: ${C.mutedLight};
    font-size: 13px;
    font-family: ${REG};
  }
  &:hover:not(:disabled):not(:focus) {
    border-color: ${(p) => (p.$err ? C.error : C.navyLine)};
  }
  &:focus {
    border-color: ${(p) => (p.$err ? C.error : C.teal)};
    background: ${C.white};
    box-shadow: 0 0 0 3.5px ${(p) => (p.$err ? C.errorPale : C.tealAlpha)};
  }
  &:disabled {
    background: ${C.surface};
    color: ${C.muted};
    cursor: not-allowed;
  }
`;

export const ErrorLine = styled.div`
  font-family: ${SEMI};
  font-size: 11px;
  color: #f44336;
  margin-top: 5px;
`;

export const InfoBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 13px 18px;
  border-radius: 10px;
  background: ${C.tealAlpha};
  border: 1px solid ${C.tealLine};
  font-family: ${REG};
  font-size: 13px;
  color: ${C.tealDark};
  line-height: 1.6;
`;

export const WarnBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 22px;
  background: ${C.navyAlpha};
  font-family: ${REG};
  font-size: 12.5px;
  color: ${C.navy};
  border-top: 1px solid ${C.navyLine};
`;

export const BannerDot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${(p) => (p.$navy ? C.navy : C.teal)};
  flex-shrink: 0;
  margin-top: ${(p) => (p.$top ? "6px" : "0")};
`;

export const Toggle = styled.div`
  width: 46px;
  height: 26px;
  border-radius: 99px;
  background: ${(p) => (p.$on ? C.teal : C.line)};
  position: relative;
  transition: background 0.22s ease;
  flex-shrink: 0;
  margin-left: 16px;
  &::after {
    content: "";
    position: absolute;
    top: 3px;
    left: ${(p) => (p.$on ? "23px" : "3px")};
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${C.white};
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
    transition: left 0.22s ease;
  }
`;

export const CheckBadge = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${C.teal};
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${checkPop} 0.3s ease both;
  box-shadow: 0 2px 8px rgba(0, 131, 140, 0.35);
  svg {
    width: 11px;
    height: 11px;
  }
`;

export const CheckIcon = () => (
  <svg viewBox="0 0 11 11" fill="none">
    <path
      d="M1.5 5.5L4 8.5L9.5 2.5"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const CheckSquare = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 7px;
  border: ${(p) => (p.$active ? "none" : `2px solid ${C.line}`)};
  background: ${(p) => (p.$active ? C.teal : C.white)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s;
  box-shadow: ${(p) =>
    p.$active ? "0 3px 12px rgba(0,131,140,0.35)" : "none"};
  ${(p) =>
    p.$active &&
    css`
      animation: ${checkPop} 0.3s ease;
    `}
  svg {
    width: 13px;
    height: 13px;
  }
`;

export const CheckSquareIcon = () => (
  <svg viewBox="0 0 13 13" fill="none">
    <path
      d="M2 6.5L5 9.5L11 3"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const StatusTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 13px;
  border-radius: 99px;
  font-family: ${SEMI};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  transition: all 0.22s;
  flex-shrink: 0;
  ${(p) =>
    p.$active
      ? css`
          background: rgba(0, 131, 140, 0.25);
          border: 1px solid rgba(0, 131, 140, 0.4);
          color: rgba(255, 255, 255, 0.85);
        `
      : css`
          background: ${C.surface};
          border: 1px solid ${C.line};
          color: ${C.muted};
        `}
`;

export const ImportantTag = styled.span`
  display: inline-block;
  margin-top: 7px;
  padding: 2px 10px;
  border-radius: 99px;
  font-family: ${SEMI};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  background: ${C.navyAlpha};
  border: 1px solid ${C.navyLine};
  color: ${C.navy};
`;

export const CardShell = styled.div`
  background: ${C.white};
  border: 1.5px solid ${C.line};
  border-radius: 14px;
  overflow: hidden;
  margin-bottom: ${(p) => p.$mb || "28px"};
`;

export const CardHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 22px;
  background: ${C.surface};
  border-bottom: 1px solid ${C.line};
`;

export const CardHeadTitle = styled.div`
  font-family: ${SEMI};
  font-weight: 700;
  font-size: 13px;
  color: ${C.navy};
  letter-spacing: -0.01em;
`;

export const ScrollBox = styled.div`
  max-height: ${(p) => p.$maxH || "380px"};
  overflow-y: auto;
  padding: 6px 0;
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${C.mutedLight};
    border-radius: 99px;
  }
`;

export const AccentStripe = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  border-radius: 0 3px 3px 0;
  background: ${(p) => (p.$navy ? C.navy : C.teal)};
`;

export const Spinner = styled.span`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
  flex-shrink: 0;
`;

/* ─── Inline step header (used inside scroll area) ────────────────────── */

export const StepHeader = styled.div`
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid ${C.line};
  animation: ${fadeUp} 0.35s ease both;
`;

export const StepHeaderEyebrow = styled.div`
  font-family: ${SEMI};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${C.teal};
  margin-bottom: 6px;
`;

export const StepHeaderTitle = styled.h2`
  font-family: ${SEMI};
  font-size: clamp(17px, 2.5vw, 22px);
  font-weight: 800;
  color: ${C.navy};
  letter-spacing: -0.025em;
  margin: 0 0 6px;
  line-height: 1.2;
`;

export const StepHeaderDesc = styled.p`
  font-family: ${REG};
  font-size: 13px;
  color: ${C.textSub};
  line-height: 1.65;
  margin: 0;
  max-width: 520px;
`;
